import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LogEntry from './LogEntry';
import PhaseSection from './PhaseSection';

const AGENT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'PM', label: 'PM' },
  { id: 'ARCHITECT', label: 'Architect' },
  { id: 'DEVELOPER', label: 'Developer' },
  { id: 'VALIDATOR', label: 'Validator' },
  { id: 'QA', label: 'QA' },
  { id: 'DEBUGGER', label: 'Debugger' },
  { id: 'DEVOPS', label: 'DevOps' },
  { id: 'TEST', label: 'Test' },
  { id: 'SANDBOX', label: 'Sandbox' },
  { id: 'DEPLOY', label: 'Deploy' },
  { id: 'INSTALL', label: 'Install' },
  { id: 'BUILD', label: 'Build' },
  { id: 'RUNTIME', label: 'Runtime' },
];

const PHASES = ['Planning', 'Architecting', 'Executing', 'Testing', 'Validating', 'QA', 'Deploying'];

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function LogTab({
  jobId,
  isGenerating,
  logs = [],
  isConnected = false,
  reconnectAttempt = 0,
  reconnectCountdown = 0,
  fatalError = null,
}) {
  const hasLogs = logs.length > 0;
  // THE BADGE REPORTS THE SOCKET, NOT THE SCROLLBACK.
  //
  // This was `isConnected || hasLogs`, so a single log line already on
  // screen made the badge read LIVE for the rest of the session — the
  // socket's actual state stopped mattering. Confirmed on a real dev
  // deployment whose nginx was stripping the WebSocket upgrade: the
  // header read "LIVE • reconnecting in 24s", a sentence that cannot be
  // true, over a log that had captured 4 events in a four-minute build.
  // The user is reading this badge to decide whether an empty log means
  // "nothing happened yet" or "you are not receiving anything", and
  // `hasLogs` answers neither question.
  const effectiveConnected = isConnected;
  // "RECONNECTING" is only honest while it actually is. Once the socket
  // has stopped for a reason retrying cannot fix — an expired session, a
  // job belonging to another account, a backend that stayed down past
  // the attempt cap — it says so instead.
  const isReconnecting = !isConnected && !fatalError && reconnectAttempt > 0;
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('LOGTAB JOB ID:', jobId, '| events:', logs.length, '| connected:', isConnected);
    }
  }, [jobId, logs.length, isConnected]);

  const listRef = useRef(null);
  const bottomRef = useRef(null);

  const [statusFilter, setStatusFilter] = useState('all'); // all | success | error
  const [selectedAgent, setSelectedAgent] = useState('all'); // all | PM | ...
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 180);

  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isAutoScrollEnabledRef = useRef(true);
  const [hasNewLogs, setHasNewLogs] = useState(false);

  const prevLogsLenRef = useRef(0);
  const lastScrollTopRef = useRef(0);

  const manualCollapsedRef = useRef({});
  const [expandedSections, setExpandedSections] = useState(() => Object.fromEntries(PHASES.map((p) => [p, true])));

  useEffect(() => {
    isAutoScrollEnabledRef.current = isAutoScrollEnabled;
  }, [isAutoScrollEnabled]);

  // Auto-expand phases that have logs (unless user manually collapsed them)
  useEffect(() => {
    if (!logs.length) return;

    const phasesWithLogs = new Set(
      logs.map((l) => (PHASES.includes(l.phase) ? l.phase : 'Executing')),
    );

    setExpandedSections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const phase of phasesWithLogs) {
        if (manualCollapsedRef.current[phase]) continue;
        if (!next[phase]) {
          next[phase] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [logs]);

  const handleTogglePhase = useCallback((phase) => {
    setExpandedSections((prev) => {
      const nextExpanded = !prev[phase];
      manualCollapsedRef.current[phase] = !nextExpanded; // collapsed => preserve
      return { ...prev, [phase]: nextExpanded };
    });
  }, []);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceToBottom < 120;

    const wasScrollTop = lastScrollTopRef.current;
    lastScrollTopRef.current = el.scrollTop;
    const scrollingUp = el.scrollTop < wasScrollTop - 2;

    if (nearBottom) {
      isAutoScrollEnabledRef.current = true;
      setIsAutoScrollEnabled(true);
      setHasNewLogs(false);
      return;
    }

    // Only pause when the user scrolls upward (prevents jitter during smooth scroll)
    if (scrollingUp) {
      isAutoScrollEnabledRef.current = false;
      setIsAutoScrollEnabled(false);
    }
  }, []);

  const handleResumeAutoScroll = useCallback(() => {
    isAutoScrollEnabledRef.current = true;
    setIsAutoScrollEnabled(true);
    setHasNewLogs(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  // Smart auto-scroll + "New logs ↓"
  useEffect(() => {
    const prevLen = prevLogsLenRef.current;
    if (logs.length <= prevLen) return;

    if (isAutoScrollEnabledRef.current) {
      setHasNewLogs(false);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else {
      setHasNewLogs(true);
    }

    prevLogsLenRef.current = logs.length;
  }, [logs]);

  useEffect(() => {
    // Initialize scroll position once logs exist
    prevLogsLenRef.current = logs.length;
  }, []);

  const filteredLogs = useMemo(() => {
    let arr = logs;

    if (statusFilter !== 'all') {
      arr = arr.filter((l) => l.type === statusFilter);
    }

    if (selectedAgent !== 'all') {
      arr = arr.filter((l) => l.agentType === selectedAgent);
    }

    const q = (debouncedSearchTerm || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter((l) => `${l.event} ${l.detail || ''}`.toLowerCase().includes(q));
    }

    return arr;
  }, [logs, statusFilter, selectedAgent, debouncedSearchTerm]);

  const groupedPhases = useMemo(() => {
    const map = new Map();
    for (const log of filteredLogs) {
      const phase = PHASES.includes(log.phase) ? log.phase : 'Executing';
      const list = map.get(phase) || [];
      list.push(log);
      map.set(phase, list);
    }

    return PHASES.filter((p) => map.has(p)).map((p) => ({
      phase: p,
      count: map.get(p)?.length || 0,
      logs: map.get(p) || [],
    }));
  }, [filteredLogs]);

  const totalVisibleCount = filteredLogs.length;

  const showReconnectCountdown =
    !isConnected && !fatalError && reconnectAttempt > 0 && reconnectCountdown > 0;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerTitle}>Live Log</span>

          <span
            style={{
              ...s.connBadge,
              ...(effectiveConnected ? s.connBadgeLive : s.connBadgeOffline),
            }}
          >
            <span style={effectiveConnected ? s.connDotLive : s.connDotOffline} />
            {effectiveConnected ? 'LIVE' : isReconnecting ? 'RECONNECTING' : 'OFFLINE'}
            {showReconnectCountdown ? ` • retrying in ${reconnectCountdown}s` : null}
            {!effectiveConnected && hasLogs ? ' • log may be incomplete' : null}
          </span>
          {fatalError ? (
            <span style={s.connFatal} title={fatalError}>{fatalError}</span>
          ) : null}
        </div>

        <div style={s.headerRight}>
          <span style={s.countBadge}>{logs.length} events</span>
        </div>
      </div>

      <div style={s.controls}>
        {/* Status + Agent + Search */}
        <div style={s.filterBar}>
          {['all', 'success', 'error'].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              style={{ ...s.filterBtn, ...(statusFilter === f ? s.filterActive : {}) }}
            >
              {f === 'all' ? 'All' : f === 'success' ? 'Success' : 'Errors'}
            </button>
          ))}

          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            style={s.agentSelect}
            aria-label="Filter by agent"
          >
            {AGENT_FILTERS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div style={s.searchRow}>
          <input
            style={s.searchInput}
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Log entries */}
      <div ref={listRef} style={s.logList} onScroll={onScroll}>
        {!jobId && logs.length === 0 ? (
          <div style={s.empty}>
            <p style={s.emptyText}>Start a generation to stream live logs.</p>
          </div>
        ) : logs.length === 0 ? (
          <div style={s.empty}>
            <p style={s.emptyText}>
              {isGenerating
                ? 'Waiting for events...'
                : effectiveConnected
                  ? 'Connected — waiting for log events.'
                  : 'No logs yet. Check that the API is running on port 8000.'}
            </p>
          </div>
        ) : totalVisibleCount === 0 ? (
          <div style={s.empty}>
            <p style={s.emptyText}>No logs match your filters/search.</p>
          </div>
        ) : groupedPhases.length > 0 ? (
          groupedPhases.map((group) => (
            <PhaseSection
              key={group.phase}
              phase={group.phase}
              count={group.count}
              expanded={!!expandedSections[group.phase]}
              onToggle={() => handleTogglePhase(group.phase)}
            >
              {group.logs.map((log, idx) => (
                <LogEntry key={log.id ?? `${group.phase}-${idx}`} log={log} />
              ))}
            </PhaseSection>
          ))
        ) : (
          filteredLogs.map((log, idx) => (
            <LogEntry key={log.id ?? idx} log={log} />
          ))
        )}

        <div ref={bottomRef} />

        {hasNewLogs && !isAutoScrollEnabled ? (
          <button type="button" style={s.newLogsBtn} onClick={handleResumeAutoScroll}>
            New logs ↓
          </button>
        ) : null}
      </div>
    </div>
  );
}

const s = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-primary)' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid var(--border)',
    gap: 10,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  headerTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  connBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 10,
    fontWeight: 900,
    padding: '2px 10px',
    borderRadius: 999,
    letterSpacing: '0.04em',
    flexShrink: 0,
  },
  connBadgeLive: { color: '#22c55e', background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.24)' },
  connBadgeOffline: { color: '#ef4444', background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.22)' },
  connDotLive: { width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' },
  connDotOffline: { width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' },
  connFatal: {
    fontSize: 11, color: '#ef4444', marginLeft: 8,
    maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  liveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    fontWeight: 800,
    color: '#ef4444',
    background: 'rgba(239,68,68,0.10)',
    padding: '2px 8px',
    borderRadius: 10,
    flexShrink: 0,
  },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1s infinite' },
  countBadge: { fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 },

  controls: { padding: '6px 16px 10px', borderBottom: '1px solid var(--border)' },
  filterBar: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  filterBtn: { padding: '4px 10px', border: 'none', background: 'transparent', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', borderRadius: 6 },
  filterActive: { background: 'rgba(99,102,241,0.12)', color: 'var(--text-primary)', border: '1px solid rgba(99,102,241,0.20)' },

  agentSelect: {
    marginLeft: 'auto',
    padding: '7px 10px',
    borderRadius: 8,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    fontSize: 12,
    outline: 'none',
    minWidth: 150,
    cursor: 'pointer',
  },

  searchRow: { marginTop: 10 },
  searchInput: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    fontSize: 13,
    outline: 'none',
  },

  logList: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
    padding: '8px 12px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  empty: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24, textAlign: 'center' },
  emptyText: { color: 'var(--text-muted)', fontSize: 13, margin: 0 },

  newLogsBtn: {
    position: 'sticky',
    bottom: 12,
    alignSelf: 'flex-end',
    marginLeft: 'auto',
    zIndex: 10,
    padding: '8px 12px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(0,0,0,0.18)',
  },
};
